import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'reactstrap';
import neoLogo from '../../../assets/images/Neo_Logo_A.svg';
import memoriaLogo from '../../../assets/images/memoria.png';

// Importando todas as imagens
import turtle from '../../../assets/images/marine_cards/marine_animal_card_1777937560942.png';
import crab from '../../../assets/images/marine_cards/marine_animal_card_crab_1777938003989.png';
import dolphin from '../../../assets/images/marine_cards/marine_animal_card_dolphin_1777938081113.png';
import jellyfish from '../../../assets/images/marine_cards/marine_animal_card_jellyfish_1777937865311.png';
import lionfish from '../../../assets/images/marine_cards/marine_animal_card_lionfish_1777938730328.png';
import mantaRay from '../../../assets/images/marine_cards/marine_animal_card_manta_ray_1777937929803.png';
import octopus from '../../../assets/images/marine_cards/marine_animal_card_octopus_1777937707348.png';
import seahorse from '../../../assets/images/marine_cards/marine_animal_card_seahorse_1777937654463.png';
import shark from '../../../assets/images/marine_cards/marine_animal_card_shark_1777938441643.png';
import whale from '../../../assets/images/marine_cards/marine_animal_card_whale_1777938274951.png';

const CARD_IMAGES = [
    { id: 'turtle', src: turtle },
    { id: 'crab', src: crab },
    { id: 'dolphin', src: dolphin },
    { id: 'jellyfish', src: jellyfish },
    { id: 'lionfish', src: lionfish },
    { id: 'mantaRay', src: mantaRay },
    { id: 'octopus', src: octopus },
    { id: 'seahorse', src: seahorse },
    { id: 'shark', src: shark },
    { id: 'whale', src: whale }
];

const MemoryGame = ({ onClose }) => {
    // states: 'intro', 'memorize', 'playing', 'result'
    const [gameState, setGameState] = useState('intro');
    const [cards, setCards] = useState([]);
    const [flippedIndexes, setFlippedIndexes] = useState([]);
    const [matches, setMatches] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isChecking, setIsChecking] = useState(false);

    // Inicializar cartas embaralhadas
    const initializeGame = useCallback(() => {
        const deck = [...CARD_IMAGES, ...CARD_IMAGES]
            .sort(() => Math.random() - 0.5)
            .map((img, index) => ({
                id: index,
                imageId: img.id,
                src: img.src,
                isFlipped: false,
                isMatched: false
            }));
        setCards(deck);
        setMatches(0);
        setFlippedIndexes([]);
    }, []);

    // Timer Loop
    useEffect(() => {
        let timer;
        if ((gameState === 'memorize' || gameState === 'playing') && timeLeft > 0) {
            timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        } else if (timeLeft === 0) {
            if (gameState === 'memorize') {
                // Fim da memorização, começa o jogo
                setGameState('playing');
                setTimeLeft(30);
                // Esconder todas as cartas
                setCards(prev => prev.map(c => ({ ...c, isFlipped: false })));
            } else if (gameState === 'playing') {
                // Fim do jogo (tempo esgotado)
                setGameState('result');
            }
        }
        return () => clearTimeout(timer);
    }, [gameState, timeLeft]);

    // Checar se todas foram encontradas
    useEffect(() => {
        if (gameState === 'playing' && matches === CARD_IMAGES.length) {
            setGameState('result');
        }
    }, [matches, gameState]);

    const startGame = () => {
        initializeGame();
        setGameState('memorize');
        setTimeLeft(20);
        // Virar todas para cima para memorizar
        setCards(prev => prev.map(c => ({ ...c, isFlipped: true })));
    };

    const handleCardClick = (index) => {
        if (gameState !== 'playing' || isChecking) return;
        if (cards[index].isFlipped || cards[index].isMatched) return;

        const newFlipped = [...flippedIndexes, index];
        setFlippedIndexes(newFlipped);

        setCards(prev => prev.map((c, i) => i === index ? { ...c, isFlipped: true } : c));

        if (newFlipped.length === 2) {
            setIsChecking(true);
            const [firstIndex, secondIndex] = newFlipped;
            
            if (cards[firstIndex].imageId === cards[secondIndex].imageId) {
                // Match
                setMatches(prev => prev + 1);
                setCards(prev => prev.map((c, i) => 
                    i === firstIndex || i === secondIndex ? { ...c, isMatched: true } : c
                ));
                setFlippedIndexes([]);
                setIsChecking(false);
            } else {
                // Não Match
                setTimeout(() => {
                    setCards(prev => prev.map((c, i) => 
                        i === firstIndex || i === secondIndex ? { ...c, isFlipped: false } : c
                    ));
                    setFlippedIndexes([]);
                    setIsChecking(false);
                }, 800);
            }
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: '#f0f2f5', // Cinza claro
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            color: '#333333', // Texto escuro
            fontFamily: 'Inter, sans-serif'
        }}>
            {/* Botão Fechar no canto superior direito */}
            <div className="position-absolute top-0 end-0 p-4" style={{ zIndex: 1000000 }}>
                <button onClick={onClose} className="btn btn-outline-dark rounded-circle p-2" style={{ width: '50px', height: '50px' }}>
                    <i className="mdi mdi-close font-size-24"></i>
                </button>
            </div>

            {/* Timer no canto superior esquerdo */}
            {(gameState === 'memorize' || gameState === 'playing') && (
                <div className="position-absolute top-0 start-0 p-4" style={{ zIndex: 1000000 }}>
                    <div className="font-size-18 fw-bold d-flex align-items-center" style={{ color: timeLeft <= 5 && gameState === 'playing' ? '#f46a6a' : '#333333' }}>
                        Tempo: <span className="ms-2" style={{ fontSize: '1.5rem', lineHeight: 1 }}>00:{timeLeft.toString().padStart(2, '0')}</span>
                    </div>
                </div>
            )}

            {/* Conteúdo Central */}
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-3">
                {gameState === 'intro' && (
                    <div className="text-center d-flex flex-column align-items-center justify-content-center" style={{ height: '100%' }}>
                        <img src={memoriaLogo} alt="Jogo da Memória" className="mb-4" style={{ maxHeight: '350px', maxWidth: '80%', objectFit: 'contain' }} />
                        <h2 className="text-primary fw-normal mb-5" style={{ lineHeight: '1.8', maxWidth: '600px' }}>
                            <strong>20 seg</strong> para memorizar, <br/>
                            <strong>30 segundos</strong> para encontrar os pares
                        </h2>
                        <Button 
                            color="primary" 
                            size="lg" 
                            onClick={startGame}
                            className="px-5 py-3 font-size-24 fw-bold rounded-pill shadow-lg mt-2"
                            style={{ letterSpacing: '1px', minWidth: '300px' }}
                        >
                            INICIAR JOGO
                        </Button>
                    </div>
                )}

                {(gameState === 'memorize' || gameState === 'playing') && (
                    <div className="w-100 h-100 d-flex flex-column pt-1">
                        <div className="text-center mb-1 d-flex flex-column align-items-center">
                            <img src={memoriaLogo} alt="Jogo da Memória" style={{ maxHeight: '240px', maxWidth: '60%', objectFit: 'contain' }} />
                        </div>

                        {/* Grid de Cartas */}
                        <div className="d-flex flex-wrap justify-content-center align-items-center align-content-center flex-grow-1 gap-2" style={{ perspective: '1000px' }}>
                            {cards.map((card, index) => (
                                <div 
                                    key={card.id} 
                                    onClick={() => handleCardClick(index)}
                                    style={{ 
                                        width: 'calc(20% - 12px)', // 5 colunas
                                        height: 'min(17vh, 160px)', // Ajuste responsivo de altura para não descer muito
                                        maxWidth: '140px',
                                        maxHeight: '200px',
                                        position: 'relative',
                                        transformStyle: 'preserve-3d',
                                        transition: 'transform 0.4s',
                                        transform: card.isFlipped || card.isMatched ? 'rotateY(180deg)' : 'rotateY(0deg)',
                                        cursor: gameState === 'playing' && !card.isMatched ? 'pointer' : 'default',
                                        opacity: 1 // Mantém 100% visível
                                    }}
                                >
                                    {/* Frente da Carta (Oculta - Verso do Baralho) */}
                                    <div style={{
                                        position: 'absolute',
                                        width: '100%', height: '100%',
                                        backfaceVisibility: 'hidden',
                                        backgroundColor: '#1c2d4a',
                                        borderRadius: '12px',
                                        border: '3px solid #2a416b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                                    }}>
                                        <img src={neoLogo} alt="NeoApp" style={{ width: '60%', filter: 'brightness(0) invert(1)', opacity: 0.8 }} />
                                    </div>
                                    
                                    {/* Verso da Carta (Imagem) */}
                                    <div style={{
                                        position: 'absolute',
                                        width: '100%', height: '100%',
                                        backfaceVisibility: 'hidden',
                                        transform: 'rotateY(180deg)',
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        border: card.isMatched ? '4px solid #34c38f' : '3px solid #f1b44c',
                                        padding: '4px',
                                        boxShadow: card.isMatched ? '0 0 15px rgba(52, 195, 143, 0.6)' : '0 4px 15px rgba(0,0,0,0.4)',
                                        backgroundImage: `url(${card.src})`,
                                        backgroundSize: 'contain',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    }}>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {gameState === 'result' && (
                    <div className="text-center px-4">
                        <i className={`mdi ${matches === CARD_IMAGES.length ? 'mdi-trophy text-warning' : 'mdi-timer-off text-danger'} mb-4`} style={{ fontSize: '120px' }}></i>
                        <h1 className="fw-bold mb-4" style={{ fontSize: '3.5rem', color: '#333333' }}>
                            {matches === CARD_IMAGES.length ? 'Incrível! Você venceu!' : 'Fim de Jogo!'}
                        </h1>
                        <p className="font-size-24 text-primary mb-5">
                            Você encontrou <strong style={{ fontSize: '3.5rem', color: '#f1b44c' }}>{matches}</strong> pares em 30 segundos.
                        </p>
                        <div className="d-flex flex-column align-items-center gap-4 mt-5">
                            <button 
                                onClick={() => setGameState('intro')}
                                className="btn btn-primary rounded-circle shadow-lg d-flex align-items-center justify-content-center"
                                style={{ width: '80px', height: '80px' }}
                                title="Jogar Novamente"
                            >
                                <i className="mdi mdi-refresh" style={{ fontSize: '40px' }}></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Footer Fino (Powered By) */}
            <div className="flex-shrink-0 w-100 position-absolute bottom-0" style={{ zIndex: 100 }}>
                <div
                    className="d-flex justify-content-end align-items-center py-3 px-4"
                >
                    <span style={{ fontSize: "11px", fontWeight: "800", color: "rgba(0,0,0,0.4)", letterSpacing: "1px" }} className="me-2 text-uppercase">POWERED BY</span>
                    <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) opacity(0.5)" }} />
                </div>
            </div>
        </div>
    );
};

export default MemoryGame;
